import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import axiosVendorInstance from '../../axiosVendorInstance';
import '../../VendorStyles/QRCodeGenerate.css';
import { toast } from 'react-toastify';
import VendorTopMenuBar from './VendorLayouts/VendorTopMenuBar';
import VendorSideMenuBar from './VendorLayouts/VendorSideMenuBar';
import MobileBackIcon from '../../assets/svg/MobileBackIcon';
import OhMyFoodLogo from '../../assets/svg/OhMyFoodLogo';
import OhMyFoodText from '../../assets/svg/OhMyFoodText';
// import omfLogo from '../../assets/OMFLogo.png';
import starPic from '../../assets/svg/GreenStar.svg';
import jsPDF from 'jspdf';
import { HashLoader } from 'react-spinners';

const QRCodeGenerate = () => {
    const [qrGenerated, setQrGenerated] = useState(false);
    const [restaurant, setRestaurant] = useState(null);
    const [qrCode, setQrCode] = useState(''); // Store QR Code data
    const [loading, setLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadCompleted, setDownloadCompleted] = useState(false);
    const cardRef = useRef();

    // const fallbackLetter = restaurant?.name && restaurant.name.toUpperCase();

    const getPublicBaseUrl = () => {
        const envUrl = process.env.REACT_APP_PUBLIC_BASE_URL;
        if (envUrl && typeof envUrl === 'string') {
            return envUrl.replace(/\/+$/, '');
        }
        return window.location.origin.replace(/\/+$/, '');
    };

    const buildDevQrUrl = (vendorId) => {
        const base = getPublicBaseUrl();
        if (!vendorId) return '';
        return `${base}/restaurant/${vendorId}`;
    };

    const fetchQRData = () => {
        setLoading(true);
        axiosVendorInstance
            .get('/vendors/account')
            .then((response) => {
                const data = response?.data?.getVendorInfo[0];
                setRestaurant({
                    id: data?.ID,
                    logo: data?.Avatar_key,
                    name: data?.Name,
                    rating: data?.menuItem_averageRating,
                    QRCode: data?.QR_Code,
                    address: `${data?.Address}, ${data?.city}, ${data?.state}, ${data?.country} - ${data?.pincode}`,
                });

                const isProd = process.env.NODE_ENV === 'production';
                let finalQr = '';
                if (isProd) {
                    finalQr = data?.QR_Code || '';
                } else {
                    finalQr = buildDevQrUrl(data?.ID) || data?.QR_Code || '';
                }

                if (finalQr) {
                    setQrCode(finalQr);
                    setQrGenerated(true);
                }
            })
            .catch((error) => console.error('Error fetching restaurant data:', error))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchQRData();
    // !qrCode && handleGenerateQR();
    }, []);

    const handleGenerateQR = async () => {
        if (!restaurant?.id) return;

        try {
            const response = await axiosVendorInstance.post('/vendors/qrcode', {
                vendorId: restaurant.id,
            });
            const isProd = process.env.NODE_ENV === 'production';
            const generated = response?.data?.qrCode;
            const finalQr = isProd ? (generated || '') : (buildDevQrUrl(restaurant.id) || generated || '');
            if (finalQr) {
                setQrCode(finalQr);
            }

            toast.success('QR Code Generated Successfully');
            setQrGenerated(true);
        } catch (error) {
            console.error('Error generating QR Code:', error);
        }
    };

    const convertImageToBase64 = async (url) => {
        const response = await fetch(url, { mode: 'cors' });
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    };

    const handleDownload = async () => {
        if (!cardRef.current) return;
        setIsDownloading(true);

        const imgElements = cardRef.current.querySelectorAll('img');
        const originalSrcMap = new Map();
        const replacedSvgs = [];

        try {
            // Convert each image to base64
            for (let img of imgElements) {
                const originalSrc = img.src;
                originalSrcMap.set(img, originalSrc);

                try {
                    const base64 = await convertImageToBase64(originalSrc);
                    img.src = base64;
                } catch (err) {
                    console.warn(`Failed to convert ${originalSrc} to base64`, err);
                }
            }

            // Convert inline SVGs to images to ensure html2canvas captures them
            const svgNodes = cardRef.current.querySelectorAll('svg');
            svgNodes.forEach((svg) => {
                try {
                    const xml = new XMLSerializer().serializeToString(svg);
                    const svg64 = window.btoa(unescape(encodeURIComponent(xml)));
                    const image64 = `data:image/svg+xml;base64,${svg64}`;
                    const img = new Image();
                    img.src = image64;
                    img.width = svg.clientWidth || parseInt(svg.getAttribute('width')) || 0;
                    img.height = svg.clientHeight || parseInt(svg.getAttribute('height')) || 0;
                    img.style.cssText = window.getComputedStyle(svg).cssText;
                    svg.parentNode.replaceChild(img, svg);
                    replacedSvgs.push({ img, svg });
                } catch (e) {
                    // ignore conversion failures and keep svg
                }
            });

            await new Promise((r) => setTimeout(r, 100));

            const canvas = await html2canvas(cardRef.current, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height],
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`${restaurant.name}_qr_card.pdf`);

            setDownloadCompleted(true);
            setTimeout(() => setDownloadCompleted(false), 3000);
        } catch (err) {
            console.error('Error generating PDF:', err);
            toast.error('Could not create PDF. Try again.');
        } finally {
            for (let [img, src] of originalSrcMap.entries()) {
                img.src = src;
            }
            // Restore any SVGs that were temporarily replaced
            replacedSvgs.forEach(({ img, svg }) => {
                try {
                    img.parentNode && img.parentNode.replaceChild(svg, img);
                } catch (e) { /* ignore */ }
            });
            setIsDownloading(false);
        }
    };

    const goBack = () => {
        window.history.back();
    };

    return (
        <div className='container'>
            <div className='vendor-header-footer'>
                <VendorTopMenuBar />
                <VendorSideMenuBar />
            </div>
            <div className='back-arrow' onClick={() => goBack()}>
                <MobileBackIcon />
            </div>
            {loading ? (
                <div className='qr-onload-loader'>
                    <HashLoader color='#E13535' loading={true} size={50} />
                </div>
            ) : !qrGenerated ? (
                <button className='generate-btn' onClick={handleGenerateQR}>
          Generate QR Code
                </button>
            ) : (
                restaurant && (
                    <>
                        <div className='card' ref={cardRef}>
                            <div className='omf-logo-rating-container'>
                                <div className='omf-logo-and-text-container1'>
                                    <OhMyFoodLogo width={35} height={40} className='omf-logo-qr'/>
                                    <div className='omf-brand-and-scan'>
                                        <OhMyFoodText width={90.69} height={24} color='white' className='oh-my-food-text11'/>
                                        {/* SCAN FOR MENU */}
                                      
                                    </div>
                                </div>
                                <div className='img-rating-restaurant'>
                                    <p className='rating-order'>
                                        {isNaN(Number(restaurant?.rating))
                                            ? '0.0'
                                            : Number(restaurant?.rating || 0.0)?.toFixed(1)}
                                    </p>
                                    <img className='star-img-order' src={starPic} alt='star' />
                                </div>
                            </div>
                            

                            <div className='qr-tile'>
                                <div className='qr-svg-wrap'>
                                    <QRCode
                                        size={320}
                                        style={{ height: '320px', width: '320px' }}
                                        value={qrCode}
                                        viewBox={'0 0 256 256'}
                                        fgColor='#FFFFFF'
                                        bgColor='#E13535'
                                    />
                                </div>
                                <div className='qr-label'>SCAN FOR MENU</div>
                            </div>
                            {process.env.NODE_ENV !== 'production' && !!qrCode && (
                                <div style={{ color: '#fff', fontSize: '12px', textAlign: 'center', marginTop: '8px', opacity: 0.85 }}>
                                    QR URL: {qrCode}
                                </div>
                            )}
                            <div className='marketing-container'>
                                <p className='promo-line'>
                                    <span className='promo-strong promo-free'>5% OFF Dine-In</span> Show App on Mobile 
                                    <span className='promo-divider'> | </span>
                                    <span className='promo-strong promo-free'>FREE Delivery</span> Order from App
                                    <span className='promo-divider'> | </span>
                                    <span className='promo-strong promo-nohike'>No Price Hikes</span> like &quot;Others&quot;
                                </p>
                                <p className='brand-download'>
                                    <span className='brand-red'>Download Oh My Food</span>
                                    <OhMyFoodLogo width={18} height={20} className='brand-logo-tiny' />
                                </p>
                                <p className='promo-sub'>Scan to Watch Reels & Recipes. Honest pricing. Fast delivery.</p>
                            </div>
                        </div>
                        <button
                            className='print-btn'
                            onClick={handleDownload}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <span>Downloading...</span>
                            ) : downloadCompleted ? (
                                <span>Downloaded QR Card</span>
                            ) : (
                                'Download QR Card'
                            )}
                        </button>
                    </>
                )
            )}
        </div>
    );
};

export default QRCodeGenerate;
